/**
 * Template Saved Banner
 *
 * Confirmation that the draft below has been published, shown above the
 * preview rather than in place of it.
 *
 * Saving used to swap the whole work area for a "Template Created
 * Successfully!" card, which unmounted the section preview and the chat at the
 * exact moment the provider was asking "did my change save?" — the reason
 * CUS-1169 reads as data loss. The confirmation now sits on top of the thing it
 * describes, and carries the appointment-type linking that GDN-2149 wants
 * available at the moment of intent.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TemplateLinkedTypesSection } from '@features/appointment-types';
import { useCan } from '@features/user';
import { CheckCircle as SavedIcon, Upload as UploadIcon } from '@mui/icons-material';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface TemplateSavedBannerProps {
  /** Template the draft was published to */
  templateId: string;
  /** Template name, for the linked-types section */
  templateName?: string;
  /** Whether the draft has been edited since that save */
  hasUnsavedEdits: boolean;
  /** Clear the session and return to the upload screen */
  onStartOver: () => void;
}

export const TemplateSavedBanner: React.FC<TemplateSavedBannerProps> = ({
  templateId,
  templateName,
  hasUnsavedEdits,
  onStartOver,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const canManageAppointmentTypes = useCan('manage_appointment_types');

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 3,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <SavedIcon sx={{ color: theme.palette.success.main }} />
        <Box sx={{ flex: 1 }}>
          {/* "Saved to your templates" is true whether this session created the
              template or edited an existing one — the builder can't tell the
              two apart, and claiming a creation that didn't happen is its own
              trust problem. */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Saved to your templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasUnsavedEdits
              ? 'You have changes since that save — use Save Template to publish them.'
              : 'The template below is what your notes will use.'}
          </Typography>
        </Box>
      </Box>

      {/* Link appointment types at the moment of intent (GDN-2149) */}
      {canManageAppointmentTypes && (
        <>
          <Divider sx={{ my: 2 }} />
          <TemplateLinkedTypesSection
            variant="compact"
            templateId={templateId}
            templateName={templateName}
            source="ai_builder_completion"
          />
        </>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        <Button variant="outlined" size="small" onClick={(): void => navigate('/templates')}>
          View All Templates
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={(): void => navigate(`/templates?id=${templateId}`)}
        >
          Open Template
        </Button>
        <Button variant="text" size="small" startIcon={<UploadIcon />} onClick={onStartOver}>
          Upload Another Template
        </Button>
      </Box>
    </Paper>
  );
};
